import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import api from '../../lib/api';
import SearchableSelect from '../../components/SearchableSelect';
import { Copy, Plus, Trash2, Power, PowerOff, RefreshCw, Bot, Building2, LayoutGrid } from 'lucide-react';

export default function OpenClawTokenManager() {
  const [tokens, setTokens] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [allWorkspaces, setAllWorkspaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newTokenName, setNewTokenName] = useState('');
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState('');
  const [createdToken, setCreatedToken] = useState(null);

  useEffect(() => {
    loadTokens();
    loadCompanies();
    loadAllWorkspaces();
  }, []);

  const loadTokens = async () => {
    try {
      setLoading(true);
      const response = await api.get('/superadmin/openclaw-tokens');
      setTokens(response.data);
    } catch (error) {
      toast.error('Failed to load OpenClaw tokens');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const loadCompanies = async () => {
    try {
      const response = await api.get('/superadmin/companies');
      setCompanies(response.data || []);
    } catch (error) {
      console.error('Failed to load companies', error);
    }
  };

  const loadAllWorkspaces = async () => {
    try {
      const response = await api.get('/superadmin/workspaces');
      setAllWorkspaces(response.data || []);
    } catch (error) {
      console.error('Failed to load workspaces', error);
    }
  };

  const handleCreateToken = async () => {
    if (!newTokenName.trim()) {
      toast.error('Token name is required');
      return;
    }
    if (!selectedWorkspaceId) {
      toast.error('Workspace is required');
      return;
    }

    try {
      const payload = {
        name: newTokenName,
        workspace_id: selectedWorkspaceId
      };
      if (selectedCompanyId) {
        payload.company_id = selectedCompanyId;
      }
      const response = await api.post('/superadmin/openclaw-tokens', payload);

      setCreatedToken(response.data);
      setNewTokenName('');
      setSelectedCompanyId('');
      setSelectedWorkspaceId('');
      toast.success('OpenClaw Token created!');
      loadTokens();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create token');
      console.error(error);
    }
  };

  const handleToggleStatus = async (tokenId, currentStatus) => {
    try {
      await api.put(`/superadmin/openclaw-tokens/${tokenId}`, {
        is_active: !currentStatus
      });

      toast.success(`Token ${!currentStatus ? 'activated' : 'deactivated'}`);
      loadTokens();
    } catch (error) {
      toast.error('Failed to update token status');
      console.error(error);
    }
  };

  const handleDeleteToken = async (tokenId, tokenName) => {
    if (!window.confirm(`Are you sure you want to delete token "${tokenName}"?\n\nNote: If an agent is linked to this token, the agent user will remain in the system.`)) {
      return;
    }

    try {
      await api.delete(`/superadmin/openclaw-tokens/${tokenId}`);
      toast.success('Token deleted');
      loadTokens();
    } catch (error) {
      toast.error('Failed to delete token');
      console.error(error);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Token copied to clipboard!');
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString('id-ID', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const selectedCompany = companies.find(c => c.id === selectedCompanyId);

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">OpenClaw API Tokens</h1>
          <p className="text-sm text-gray-600 mt-1">
            Manage tokens for OpenClaw AI Agent integration. Each token links to one workspace and one agent.
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
        >
          <Plus size={18} />
          Create Token
        </button>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <RefreshCw className="animate-spin mx-auto mb-2" size={32} />
          <p className="text-gray-600">Loading tokens...</p>
        </div>
      ) : tokens.length === 0 ? (
        <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg p-12 text-center">
          <Bot className="mx-auto mb-4 text-gray-400" size={48} />
          <p className="text-gray-600 mb-2">No OpenClaw Tokens yet</p>
          <p className="text-sm text-gray-500 mb-4">
            Create a token to allow external AI agents to connect to a workspace
          </p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
          >
            Create First Token
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-x-auto">
          <table className="min-w-[900px] w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[140px]">
                  Name
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[200px]">
                  Token
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[180px]">
                  Company / Workspace
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[110px]">
                  Agent
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[90px]">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[130px]">
                  Last Used
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-[80px]">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {tokens.map((token) => (
                <tr key={token.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{token.name}</div>
                    <div className="text-xs text-gray-500">by {token.created_by}</div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <code className="text-xs bg-gray-100 px-2 py-1 rounded font-mono truncate max-w-[140px] block">
                        {token.token.substring(0, 24)}...
                      </code>
                      <button
                        onClick={() => copyToClipboard(token.token)}
                        className="p-1 hover:bg-gray-200 rounded flex-shrink-0"
                        title="Copy token"
                      >
                        <Copy size={14} className="text-gray-600" />
                      </button>
                    </div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="text-sm text-gray-900 flex items-center gap-1">
                      <Building2 size={14} className="text-gray-400 flex-shrink-0" />
                      <span className="truncate max-w-[140px] block" title={token.company_name || token.company_id || 'No company'}>
                        {token.company_name || token.company_id || <span className="text-gray-400 italic">No company</span>}
                      </span>
                    </div>
                    <div className="text-xs text-gray-500 flex items-center gap-1 mt-0.5">
                      <LayoutGrid size={12} className="text-gray-400 flex-shrink-0" />
                      <span className="truncate max-w-[140px] block" title={token.workspace_name || token.workspace_id}>
                        {token.workspace_name || token.workspace_id}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    {token.openclaw_agent_id ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        <Bot size={12} />
                        Linked
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                        Not registered
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    {token.is_active ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        <Power size={12} />
                        Active
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                        <PowerOff size={12} />
                        Inactive
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                    {formatDate(token.last_used_at)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => handleToggleStatus(token.id, token.is_active)}
                      className={`mr-2 ${
                        token.is_active ? 'text-orange-600 hover:text-orange-900' : 'text-green-600 hover:text-green-900'
                      }`}
                      title={token.is_active ? 'Deactivate' : 'Activate'}
                    >
                      {token.is_active ? <PowerOff size={16} /> : <Power size={16} />}
                    </button>
                    <button
                      onClick={() => handleDeleteToken(token.id, token.name)}
                      className="text-red-600 hover:text-red-900"
                      title="Delete token"
                    >
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Token Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h2 className="text-xl font-bold mb-4">Create OpenClaw Token</h2>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Token Name
              </label>
              <input
                type="text"
                value={newTokenName}
                onChange={(e) => setNewTokenName(e.target.value)}
                placeholder="e.g., Sarah AI Production"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                autoFocus
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Company <span className="text-gray-400 font-normal">(Optional)</span>
              </label>
              <SearchableSelect
                value={selectedCompanyId}
                onChange={(val) => {
                  setSelectedCompanyId(val);
                  setSelectedWorkspaceId('');
                }}
                options={[
                  { value: '', label: 'All companies / No company' },
                  ...companies.map((c) => ({ value: c.id, label: c.name })),
                ]}
                placeholder="Select company..."
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Workspace <span className="text-red-500">*</span>
              </label>
              <SearchableSelect
                value={selectedWorkspaceId}
                onChange={(val) => setSelectedWorkspaceId(val)}
                options={allWorkspaces
                  .filter((ws) => !selectedCompanyId || ws.company_id === selectedCompanyId || (!ws.company_id && !selectedCompanyId))
                  .map((ws) => {
                    const companyName = companies.find((c) => c.id === ws.company_id)?.name;
                    return {
                      value: ws.id,
                      label: ws.name + (companyName ? ` (${companyName})` : ' (No company)'),
                    };
                  })}
                placeholder="Select workspace..."
              />
            </div>

            {selectedCompany ? (
              <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3 mb-4">
                <p className="text-xs text-indigo-700">
                  Token will be linked to <strong>{selectedCompany.name}</strong>.
                  After creation, share the token with the OpenClaw integrator. They must call
                  <code className="mx-1 bg-indigo-100 px-1 rounded">POST /api/openclaw/register</code>
                  to link an AI Agent.
                </p>
              </div>
            ) : (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-4">
                <p className="text-xs text-gray-600">
                  No company selected. Token will be linked to a <strong>standalone workspace</strong> (without company).
                  After creation, share the token with the OpenClaw integrator.
                </p>
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setNewTokenName('');
                  setSelectedCompanyId('');
                  setSelectedWorkspaceId('');
                  setCreatedToken(null);
                }}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateToken}
                className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
              >
                Create Token
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Show Created Token Modal */}
      {createdToken && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4">
            <h2 className="text-xl font-bold mb-4 text-green-600">✅ OpenClaw Token Created!</h2>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
              <p className="text-sm font-medium text-yellow-800 mb-2">
                ⚠️ Important: Copy this token now!
              </p>
              <p className="text-xs text-yellow-700">
                You won't be able to see the full token again. Share it securely with your OpenClaw integrator.
              </p>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Token Name
              </label>
              <div className="text-lg font-semibold">{createdToken.name}</div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                OpenClaw API Token
              </label>
              <div className="flex items-center gap-2 bg-gray-50 p-3 rounded-lg">
                <code className="flex-1 text-sm font-mono break-all">
                  {createdToken.token}
                </code>
                <button
                  onClick={() => copyToClipboard(createdToken.token)}
                  className="px-3 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 flex items-center gap-2"
                >
                  <Copy size={16} />
                  Copy
                </button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                <p className="text-xs font-medium text-gray-500">Company ID</p>
                <code className="text-sm text-gray-700">{createdToken.company_id || <span className="text-gray-400 italic">None</span>}</code>
              </div>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                <p className="text-xs font-medium text-gray-500">Workspace ID</p>
                <code className="text-sm text-gray-700">{createdToken.workspace_id}</code>
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <p className="text-sm font-medium text-blue-800 mb-2">
                🤖 Share with OpenClaw Integrator
              </p>
              <div className="text-xs text-blue-700 space-y-1">
                <p><strong>Base URL:</strong> {window.location.origin}</p>
                <p><strong>Token:</strong> {createdToken.token.substring(0, 30)}...</p>
                <p><strong>Workspace ID:</strong> {createdToken.workspace_id}</p>
                <p className="mt-2">Next step: integrator calls <code className="bg-blue-100 px-1 rounded">POST /api/openclaw/register</code> with this token</p>
              </div>
            </div>

            <button
              onClick={() => {
                setCreatedToken(null);
                setShowCreateModal(false);
              }}
              className="w-full px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
